import React from "react";
import { useTranslation } from "react-i18next";
import RichText from "components/basic/richText";
import { Box, Link, useTheme } from "@mui/material";
import Button from "components/basic/button";
import Typography from "components/basic/typography";
import { useGoogleLoginMutation, useMeQuery } from "generated/graphql";
import { useCallback, useEffect, useRef } from "react";
import useOffsetTop from "hooks/useOffsetTop";
import { useNavigate } from "react-router-dom";
import GoogleIcon from "@mui/icons-material/Google";
import AppleIcon from "@mui/icons-material/Apple";
import ChargerImage from "resources/images/ChargerImage.png";
import { useGoogleLogin } from "@react-oauth/google";
import { useSnack } from "hooks/useSnack";
import { Account } from "../..";

interface SignUpMethodProps {
  setAccount: React.Dispatch<React.SetStateAction<Account>>;
}

const SignUpMethod: React.FC<SignUpMethodProps> = ({ setAccount }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [{ data: loggedInUser, fetching }] = useMeQuery();
  const [, googleLoginMutation] = useGoogleLoginMutation();
  const showSnack = useSnack();
  const { t } = useTranslation("auth");
  const { t: tCommon } = useTranslation();
  const rootRef = useRef<HTMLDivElement>(null);
  const offsetTop = useOffsetTop(rootRef);

  const googleLogin = useGoogleLogin({
    // Otherwise Google silently reuses the last account, leaving people with
    // several Google accounts no way to pick another.
    prompt: "select_account",
    onSuccess: async (tokenResponse) => {
      const response = await fetch(
        "https://www.googleapis.com/oauth2/v3/userinfo",
        {
          headers: { Authorization: `Bearer ${tokenResponse.access_token}` },
        },
      );
      // Only pre-fills the form; the server checks the token with Google.
      const userInfo = await response.json();

      // First try sign-in: if account already exists, log in instead of re-running sign-up flow
      const existingAccountResult = await googleLoginMutation(
        { accessToken: tokenResponse.access_token },
        { requestPolicy: "network-only" },
      );

      if (existingAccountResult.data?.googleLogin.account) {
        const firstName =
          existingAccountResult.data.googleLogin.account.firstName ||
          userInfo.given_name ||
          "there";

        showSnack(t("method.welcome_back", { name: firstName }), "success");
        navigate("/");
        return;
      }

      const loginErrorMessage =
        existingAccountResult.data?.googleLogin.errors?.[0]?.message;

      // Only continue with registration if account truly doesn't exist
      if (loginErrorMessage && loginErrorMessage !== t("method.no_account")) {
        showSnack(loginErrorMessage, "error");
        return;
      }

      // Pre-fill account state with Google data and navigate into the sign-up flow
      setAccount((prev) => ({
        ...prev,
        firstName: userInfo.given_name || "",
        lastName: userInfo.family_name || "",
        email: userInfo.email || "",
        profilePhotoUrl: userInfo.picture || null,
        googleAccessToken: tokenResponse.access_token,
        authProvider: "google",
      }));

      showSnack(
        `Welcome ${userInfo.given_name}! Let's finish setting up your account.`,
        "success",
      );
      navigate("?currentPage=nameForm");
    },
    onError: (error) =>
      showSnack(t("method.google_failed", { error }), "error"),
  });

  const handleHomePageRedirect = useCallback(() => {
    navigate("/");
  }, [navigate]);

  // If user is already logged in and somehow they happen upon this page
  // Then send them back to landing page
  useEffect(() => {
    if (fetching) {
      return;
    }

    if (!loggedInUser?.me) {
      return;
    }

    handleHomePageRedirect();
  }, [fetching, handleHomePageRedirect, loggedInUser]);

  return (
    <Box
      ref={rootRef}
      sx={{
        display: "flex",
        minHeight: `calc(100dvh - ${offsetTop}px)`,
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: "hidden",
          zIndex: -1,
          display: { xs: "none", md: "flex" },
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={ChargerImage}
          alt={t("method.image_alt")}
          style={{
            width: "75%",
            height: "75%",
            objectFit: "contain",
          }}
        />
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          textAlign: "center",
          px: 2,
          py: { xs: 4, md: 3 },
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: theme.spacing(2),
            width: "100%",
            maxWidth: "450px",
          }}
        >
          <Typography
            variant="h1"
            align="center"
            style={{
              fontWeight: "bold",
              marginBottom: theme.spacing(2),
            }}
            sx={{ fontSize: { xs: "2.25em", sm: "3em" } }}
          >
            {t("method.title")}
          </Typography>

          <Button
            onClick={() => googleLogin()}
            variant="contained"
            startAdornment={<GoogleIcon />}
          >
            {t("common.continue_google")}
          </Button>

          <Button
            onClick={() => {}}
            variant="contained"
            startAdornment={<AppleIcon />}
            disabled
          >
            {t("common.continue_apple")}
          </Button>

          <Typography variant="body1" align="center">
            {tCommon("ui.or")}
          </Typography>

          <Button
            onClick={() => navigate("?currentPage=emailForm")}
            variant="outlined"
          >
            {t("method.with_email")}
          </Button>

          {/* Terms is intentionally not a link: no such page exists yet,
                  and pointing at a missing one is worse than plain text. */}
          <Typography variant="body2">
            <RichText
              t={t}
              i18nKey="method.terms"
              components={{
                a: (
                  <Link
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="always"
                  />
                ),
              }}
            />
          </Typography>

          <Button onClick={() => navigate("/signin")} variant="text">
            <RichText
              t={t}
              i18nKey="common.have_account"
              components={{
                a: (
                  <span
                    style={{ color: "orange", marginLeft: theme.spacing(1) }}
                  />
                ),
              }}
            />
          </Button>
        </div>
      </Box>
    </Box>
  );
};

export default SignUpMethod;
